<template>
  <div class="revolve-cut-property" data-testid="revolve-cut-property-section">
    <n-collapse :default-expanded-names="['cut-side']" class="pm-collapse">
      <n-collapse-item title="切除侧" name="cut-side">
        <n-checkbox
          :checked="flipSideToCut"
          @update:checked="updateFlipSideToCut"
        >
          反侧切除
        </n-checkbox>
      </n-collapse-item>
    </n-collapse>

    <RevolvePropertySection :context="revolveContext" />

    <RevolveCutKeepBodiesDialog
      :show="keepBodiesDialog.visible"
      :fragments="cutFragments"
      :selection-mode="keepBodiesDialog.selectionMode"
      :draft-selected-shape-keys="keepBodiesDialog.draftSelectedShapeKeys"
      @update:selection-mode="updateKeepBodiesSelectionMode"
      @toggle-fragment="toggleKeepBodiesDraftFragment"
      @confirm="confirmKeepBodiesDialog"
      @cancel="cancelKeepBodiesDialog"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  NCheckbox,
  NCollapse,
  NCollapseItem,
} from 'naive-ui';
import type { PropertyManagerContext } from '@/core';
import RevolvePropertySection from '@/components/editor/property-manager/revolve/RevolvePropertySection.vue';
import RevolveCutKeepBodiesDialog from '@/components/editor/RevolveCutKeepBodiesDialog.vue';

interface RevolveCutPropertySectionProps {
  /** PropertyManager 上下文。 */
  context: PropertyManagerContext;
}

const props = defineProps<RevolveCutPropertySectionProps>();

const flipSideToCut = computed(() => props.context.revolveCutState?.flipSideToCut === true);
const cutFragments = computed(() => props.context.revolveCutState?.cutFragments ?? []);
const keepBodiesDialog = computed(() => props.context.revolveCutState?.keepBodiesDialog ?? {
  visible: false,
  selectionMode: 'all' as const,
  draftSelectedShapeKeys: {},
});

/**
 * 把旋转切除上下文投影为通用旋转属性面板上下文。
 */
const revolveContext = computed<PropertyManagerContext>(() => ({
  ...props.context,
  revolveState: props.context.revolveCutState,
  revolveActive: props.context.revolveCutActive,
  revolveSelectedContours: props.context.revolveCutState?.selectedContours ?? [],
  revolveSelectedAxisLabel: props.context.revolveCutState?.selectedAxisLabel ?? null,
  revolveDirectionReversed: props.context.revolveCutState?.directionReversed === true,
  revolveDirection1Angle: props.context.revolveCutState?.direction1Angle,
  revolveDirection2Enabled: props.context.revolveCutState?.direction2Enabled === true,
  revolveDirection2Angle: props.context.revolveCutState?.direction2Angle,
  revolvePickerTarget: props.context.revolveCutState?.pickerTarget ?? null,
  submitRevolve: props.context.submitRevolveCut,
  cancelRevolve: props.context.cancelRevolveCut,
  activateRevolvePicker: props.context.activateRevolveCutPicker,
  clearRevolveSelectedAxis: props.context.clearRevolveCutSelectedAxis,
  clearRevolveSelectedContours: props.context.clearRevolveCutSelectedContours,
  removeRevolveSelectedContour: props.context.removeRevolveCutSelectedContour,
  highlightRevolveSelectedAxis: props.context.highlightRevolveCutSelectedAxis,
  highlightRevolveSelectedContour: props.context.highlightRevolveCutSelectedContour,
  setRevolveDirection1Angle: props.context.setRevolveCutDirection1Angle,
  setRevolveDirection2Enabled: props.context.setRevolveCutDirection2Enabled,
  setRevolveDirection2Angle: props.context.setRevolveCutDirection2Angle,
  setRevolveDirectionReversed: props.context.setRevolveCutDirectionReversed,
}));

/**
 * 更新反侧切除状态。
 * @param value 勾选值。
 */
function updateFlipSideToCut(value: boolean): void {
  props.context.setRevolveCutFlipSideToCut?.(value);
}

/**
 * 更新保留实体弹窗选择模式。
 * @param mode 选择模式。
 */
function updateKeepBodiesSelectionMode(mode: 'all' | 'selected'): void {
  props.context.setRevolveCutKeepBodiesSelectionMode?.(mode);
}

/**
 * 切换保留实体弹窗草稿选择。
 * @param sourceSolidId 来源实体 ID。
 * @param shapeKey 片段稳定键。
 */
function toggleKeepBodiesDraftFragment(sourceSolidId: string, shapeKey: string): void {
  props.context.toggleRevolveCutKeepBodiesDraftFragment?.(sourceSolidId, shapeKey);
}

/**
 * 确认保留实体弹窗。
 */
function confirmKeepBodiesDialog(): void {
  props.context.confirmRevolveCutKeepBodiesDialog?.();
}

/**
 * 取消保留实体弹窗。
 */
function cancelKeepBodiesDialog(): void {
  props.context.cancelRevolveCutKeepBodiesDialog?.();
}
</script>

<style scoped>
.revolve-cut-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
</style>
